import React, { useState } from 'react';
import {
  ChevronDown,
  Shield,
  CheckCircle,
  Info,
  Database,
  Server,
  BookOpen,
  Activity,
  FileText,
  Search,
  TrendingUp,
  Layers,
  FileCheck
} from 'lucide-react';
import {
  ALL_FRAMEWORKS,
  FRAMEWORK_SUMMARY,
  TOTAL_CONTROLS,
  PLATFORM_ARCHITECTURE,
  FRAMEWORK_CATEGORIES
} from './CompleteFrameworkData';

const ComprehensiveFrameworkView = () => {
  const [expandedFrameworks, setExpandedFrameworks] = useState({});
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const toggleFramework = (frameworkId) => {
    setExpandedFrameworks(prev => ({
      ...prev,
      [frameworkId]: !prev[frameworkId]
    }));
  };

  const renderControlTable = (controls) => {
    return (
      <div className="control-table-wrapper">
        <table className="control-table">
          <thead>
            <tr>
              <th>Control ID</th>
              <th>Requirement</th>
              <th>Scan</th>
              <th>Collection</th>
              <th>Technical Test</th>
              <th>Pass Criteria</th>
            </tr>
          </thead>
          <tbody>
            {controls.filter(control => {
              if (!searchTerm) return true;
              const search = searchTerm.toLowerCase();
              return (
                control.id?.toLowerCase().includes(search) ||
                control.requirement?.toLowerCase().includes(search) ||
                control.scan?.toLowerCase().includes(search)
              );
            }).map((control, index) => (
              <tr key={index}>
                <td>
                  <span className="control-id-badge">
                    {control.id || control.controlId}
                  </span>
                </td>
                <td>
                  <div className="requirement-text">
                    {control.requirement || control.title}
                  </div>
                </td>
                <td>
                  <div className="scan-badges">
                    {control.scan.split(',').map((scan, i) => (
                      <span key={i} className="scan-badge">
                        {scan.trim()}
                      </span>
                    ))}
                  </div>
                </td>
                <td>
                  <div className="collection-text">
                    {control.collection}
                  </div>
                </td>
                <td>
                  <div className="technical-text">{control.technicalTest}</div>
                </td>
                <td>
                  <div className="pass-criteria">
                    <CheckCircle className="check-icon" />
                    <div>{control.passCriteria}</div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="compliance-page">
      {/* Page Header */}
      <div className="page-header">
        <div className="header-content">
          <div className="header-title-row">
            <Shield className="header-icon" />
            <h1>Comprehensive Compliance Control Mapping</h1>
          </div>
          <p className="header-subtitle">
            Complete mapping of {TOTAL_CONTROLS} compliance controls across {FRAMEWORK_SUMMARY.totalFrameworks} federal frameworks
          </p>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="summary-section">
        <div className="section-header">
          <TrendingUp className="section-icon" />
          <h2>Framework Summary</h2>
        </div>
        <div className="stats-grid">
          <div className="stat-card stat-card-blue">
            <div className="stat-icon-wrapper">
              <FileText className="stat-icon" />
            </div>
            <div className="stat-content">
              <div className="stat-number">{FRAMEWORK_SUMMARY.totalFrameworks}</div>
              <div className="stat-label">Total Frameworks</div>
            </div>
          </div>
          <div className="stat-card stat-card-green">
            <div className="stat-icon-wrapper">
              <Shield className="stat-icon" />
            </div>
            <div className="stat-content">
              <div className="stat-number">{TOTAL_CONTROLS}</div>
              <div className="stat-label">Total Controls</div>
            </div>
          </div>
          <div className="stat-card stat-card-purple">
            <div className="stat-icon-wrapper">
              <Server className="stat-icon" />
            </div>
            <div className="stat-content">
              <div className="stat-number">25+</div>
              <div className="stat-label">Scanning Tools</div>
            </div>
          </div>
        </div>
      </div>

      {/* Platform Architecture */}
      <div className="platform-section">
        <div className="section-header">
          <Server className="section-icon" />
          <h2>Platform Architecture Context</h2>
        </div>
        <div className="architecture-grid">
          <div className="architecture-card arch-blue">
            <div className="arch-icon-wrapper arch-icon-blue">
              <Database className="arch-icon" />
            </div>
            <div className="arch-content">
              <div className="arch-title">Scanning Engine</div>
              <div className="arch-description">{PLATFORM_ARCHITECTURE.scanningEngine}</div>
            </div>
          </div>

          <div className="architecture-card arch-green">
            <div className="arch-icon-wrapper arch-icon-green">
              <Database className="arch-icon" />
            </div>
            <div className="arch-content">
              <div className="arch-title">Data Storage</div>
              <div className="arch-description">{PLATFORM_ARCHITECTURE.dataStorage}</div>
            </div>
          </div>

          <div className="architecture-card arch-purple">
            <div className="arch-icon-wrapper arch-icon-purple">
              <Activity className="arch-icon" />
            </div>
            <div className="arch-content">
              <div className="arch-title">Real-time Tracking</div>
              <div className="arch-description">{PLATFORM_ARCHITECTURE.realTimeTracking}</div>
            </div>
          </div>

          <div className="architecture-card arch-orange">
            <div className="arch-icon-wrapper arch-icon-orange">
              <BookOpen className="arch-icon" />
            </div>
            <div className="arch-content">
              <div className="arch-title">Output Generation</div>
              <div className="arch-description">{PLATFORM_ARCHITECTURE.outputGeneration}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Framework Categories */}
      <div className="categories-section">
        <div className="section-header">
          <Layers className="section-icon" />
          <h2>Framework Categories</h2>
        </div>
        <div className="categories-grid">
          {Object.entries(FRAMEWORK_CATEGORIES).map(([key, category]) => (
            <div
              key={key}
              className={`category-card ${activeCategory === key ? 'category-active' : ''}`}
              onClick={() => setActiveCategory(activeCategory === key ? 'all' : key)}
            >
              <h3>{category.name}</h3>
              <p className="category-description">{category.description}</p>
              <div className="category-frameworks">
                {category.frameworks.map((fw, i) => (
                  <span key={i} className="framework-tag">
                    {fw}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Search Bar */}
      <div className="search-section">
        <div className="search-wrapper">
          <Search className="search-icon" />
          <input
            type="text"
            placeholder="Search controls by ID, requirement, or scan..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* Frameworks List */}
      <div className="frameworks-section">
        <div className="section-header">
          <Shield className="section-icon" />
          <h2>Compliance Frameworks & Controls</h2>
        </div>

        <div className="frameworks-list">
          {Object.entries(ALL_FRAMEWORKS).map(([key, framework]) => {
            const isExpanded = expandedFrameworks[key];
            const categoryMatch = !activeCategory || activeCategory === 'all' ||
              FRAMEWORK_CATEGORIES[activeCategory]?.frameworks.some(fw =>
                framework.name.includes(fw) || fw.includes(framework.name.split('(')[0].trim())
              );

            if (!categoryMatch) return null;

            return (
              <div key={key} className="framework-card">
                <div
                  className="framework-header"
                  onClick={() => toggleFramework(key)}
                >
                  <div className="framework-header-left">
                    <ChevronDown className={`chevron-icon ${isExpanded ? 'chevron-expanded' : ''}`} />
                    <div className="framework-shield">
                      <Shield className="shield-icon" />
                    </div>
                    <div className="framework-info">
                      <h3>{framework.name}</h3>
                      {framework.primaryScans && (
                        <p className="framework-scans">
                          Primary Scans: <span>{framework.primaryScans}</span>
                        </p>
                      )}
                      {framework.description && (
                        <p className="framework-desc">{framework.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="framework-count">
                    <FileCheck className="count-icon" />
                    <span>{framework.controls.length} controls</span>
                  </div>
                </div>

                {isExpanded && (
                  <div className="framework-content">
                    {renderControlTable(framework.controls)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="page-footer">
        <div className="footer-content">
          <div className="footer-badge">
            <Info className="footer-icon" />
            <span>Comprehensive Compliance Platform</span>
          </div>
          <p>
            This comprehensive mapping leverages 25+ scanning tools to provide automated validation
            for hundreds of compliance controls across major federal frameworks.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ComprehensiveFrameworkView;
